import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PublicSEO from '../../components/public/PublicSEO';
import MarketplaceSearchForm from '../../components/public/MarketplaceSearchForm';
import { PayerTag, ModeTag, SlotButton } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { publicPath } from '../../utils/canonicalUrl';
import { formatPrice } from '../../utils/format';
import { getMedicoSlug, formatSlotTime } from '../../utils/slug';
import { saveMedicoBookingIntent } from '../../utils/bookingIntent';
import './RicercaPage.css';

function initials(nome, cognome) {
  const a = (nome || '').trim().charAt(0);
  const b = (cognome || '').trim().charAt(0);
  return ((a + b) || '?').toUpperCase();
}

function isSaturdaySlot(slot) {
  if (!slot?.data) return false;
  const d = new Date(`${String(slot.data).slice(0, 10)}T12:00:00`);
  return d.getDay() === 6;
}

/** Chip compatto: "Oggi 8:00" / "Domani 9:30" / solo orario (niente stringhe lunghe). */
function slotChipLabel(slot) {
  const time = formatSlotTime(slot?.oraInizio) || '';
  const raw = String(slot?.label || '').trim();
  if (/oggi/i.test(raw)) return time ? `Oggi ${time}` : raw;
  if (/domani/i.test(raw)) return time ? `Domani ${time}` : raw;
  if (raw && raw.length <= 14) return raw;
  return time || raw || '—';
}

/**
 * Risultati ricerca marketplace (mockup 01_ricerca_risultati).
 */
export default function RicercaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPaziente } = useAuth();

  const [prestazione, setPrestazione] = useState(searchParams.get('prestazione') || '');
  const [dove, setDove] = useState(searchParams.get('dove') || '');
  const [pagatore, setPagatore] = useState(() => {
    const p = searchParams.get('pagatore') || 'PRIVATO';
    return p === 'FONDO' ? 'PRIVATO' : p;
  });
  const [idFondo, setIdFondo] = useState('');
  const [modalita, setModalita] = useState(searchParams.get('modalita') || 'tutte');
  const [sort, setSort] = useState(searchParams.get('sort') || 'disponibilita');
  const [entro7, setEntro7] = useState(searchParams.get('entro7') === '1');
  const [soloSabato, setSoloSabato] = useState(searchParams.get('sabato') === '1');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const queryKey = useMemo(() => {
    const rawPagatore = searchParams.get('pagatore') || 'PRIVATO';
    return {
      prestazione: searchParams.get('prestazione') || '',
      pagatore: rawPagatore === 'FONDO' ? 'PRIVATO' : rawPagatore,
      idFondo: '',
      modalita: searchParams.get('modalita') || 'tutte',
      sort: searchParams.get('sort') || 'disponibilita',
      raggioKm: searchParams.get('raggioKm') || '',
      lat: searchParams.get('lat') || '',
      lng: searchParams.get('lng') || '',
      entro7: searchParams.get('entro7') === '1',
      sabato: searchParams.get('sabato') === '1',
    };
  }, [searchParams]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        sort: queryKey.sort,
      };
      if (queryKey.raggioKm) params.raggioKm = Number(queryKey.raggioKm) || 50;
      if (queryKey.lat && queryKey.lng) {
        params.lat = Number(queryKey.lat);
        params.lng = Number(queryKey.lng);
      }
      if (queryKey.prestazione) params.prestazione = queryKey.prestazione;
      if (queryKey.pagatore && queryKey.pagatore !== 'TUTTI') params.pagatore = queryKey.pagatore;
      if (queryKey.idFondo) params.idFondo = Number(queryKey.idFondo);
      if (queryKey.modalita && queryKey.modalita !== 'tutte') params.modalita = queryKey.modalita;
      const res = await api.get('/public/search', { params });
      let payload = res.data || { results: [], total: 0, strutture: 0 };
      if (queryKey.entro7 && Array.isArray(payload.results)) {
        const limit = new Date();
        limit.setDate(limit.getDate() + 7);
        const limitIso = limit.toISOString().slice(0, 10);
        const filtered = payload.results.filter((h) => h.primaDisponibilita && String(h.primaDisponibilita).slice(0, 10) <= limitIso);
        payload = { ...payload, results: filtered, total: filtered.length };
      }
      if (queryKey.sabato && Array.isArray(payload.results)) {
        const filtered = payload.results
          .map((h) => ({
            ...h,
            prossimiSlot: (h.prossimiSlot || []).filter(isSaturdaySlot),
          }))
          .filter((h) => (h.prossimiSlot || []).length > 0
            || (h.primaDisponibilita && new Date(`${String(h.primaDisponibilita).slice(0, 10)}T12:00:00`).getDay() === 6));
        payload = { ...payload, results: filtered, total: filtered.length };
      }
      setData(payload);
    } catch (e) {
      console.error(e);
      setError('Impossibile completare la ricerca.');
      setData({ results: [], total: 0, strutture: 0 });
    } finally {
      setLoading(false);
    }
  }, [queryKey]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  const setChip = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'modalita') {
      next.set('modalita', value);
      setModalita(value);
    }
    if (key === 'entro7') {
      if (value) next.set('entro7', '1');
      else next.delete('entro7');
      setEntro7(!!value);
    }
    if (key === 'sabato') {
      if (value) next.set('sabato', '1');
      else next.delete('sabato');
      setSoloSabato(!!value);
    }
    setSearchParams(next);
  };

  const bookSlot = (hit, slot) => {
    const medico = {
      id: hit.idMedico,
      nome: hit.medicoNome,
      cognome: hit.medicoCognome,
    };
    const slug = hit.slug || getMedicoSlug(medico);
    const intent = saveMedicoBookingIntent(
      medico,
      slug,
      slot.data,
      { oraInizio: slot.oraInizio, oraFine: slot.oraFine, disponibile: true },
      {
        idTariffa: hit.idTariffa,
        durataMin: hit.durataMinuti,
        isVideo: hit.isVideo,
        nomePrestazione: hit.nomePrestazione,
        importo: hit.prezzo,
        pagatore: hit.pagatore || pagatore,
        idFondo: hit.idFondo || (idFondo ? Number(idFondo) : null),
        canale: 'MARKETPLACE',
        idStruttura: hit.idStruttura,
        idSede: hit.idSede,
        modalita: hit.isVideo ? 'VIDEO' : 'STUDIO',
      }
    );
    if (isAuthenticated() && isPaziente()) {
      navigate(intent.path, { state: intent.state });
    } else {
      navigate('/login', { state: { afterLogin: intent } });
    }
  };

  const results = data?.results || [];
  const titlePrestazione = queryKey.prestazione || 'Visite ed esami';
  const pageTitle = `${titlePrestazione} vicino a Pistoia`;

  return (
    <div className="ricerca-page">
      <PublicSEO
        title={`${pageTitle} | Virtual Clinic`}
        description={`Trova disponibilità per ${titlePrestazione} vicino a Pistoia. Prezzi finali e orari aggiornati.`}
        canonicalPath="/ricerca/"
      />

      <section className="ricerca-page__searchbar">
        <div className="ricerca-page__inner">
          <MarketplaceSearchForm
            initial={{ prestazione, dove, pagatore, idFondo }}
            onSubmitLocal={(next) => {
              next.set('modalita', modalita);
              next.set('sort', sort);
              if (entro7) next.set('entro7', '1');
              if (soloSabato) next.set('sabato', '1');
              setPrestazione(next.get('prestazione') || '');
              setDove(next.get('dove') || '');
              setPagatore(next.get('pagatore') || 'PRIVATO');
              setIdFondo(next.get('idFondo') || '');
              setSearchParams(next);
            }}
          />

          <div className="ricerca-page__chips">
            <span className="ricerca-page__chips-label">Modalità</span>
            {['tutte', 'studio', 'video'].map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={modalita === m}
                className={modalita === m ? 'is-active' : ''}
                onClick={() => setChip('modalita', m)}
              >
                {m === 'tutte' ? 'Tutte' : m === 'studio' ? 'In studio' : 'In video'}
              </button>
            ))}
            <span className="ricerca-page__sep" aria-hidden="true" />
            <button
              type="button"
              aria-pressed={entro7}
              className={entro7 ? 'is-active' : ''}
              onClick={() => setChip('entro7', !entro7)}
            >
              Entro 7 giorni
            </button>
            <button
              type="button"
              aria-pressed={soloSabato}
              className={soloSabato ? 'is-active' : ''}
              onClick={() => setChip('sabato', !soloSabato)}
            >
              Sabato
            </button>
          </div>
        </div>
      </section>

      <main className="ricerca-page__main">
        <div className="ricerca-page__results">
          <div className="ricerca-page__results-head">
            <h1>{pageTitle}</h1>
            <label className="ricerca-page__sort">
              Ordina per
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  const next = new URLSearchParams(searchParams);
                  next.set('sort', e.target.value);
                  setSearchParams(next);
                }}
              >
                <option value="disponibilita">Prima disponibilità</option>
                <option value="prezzo">Prezzo</option>
                <option value="distanza">Distanza</option>
              </select>
            </label>
          </div>
          <p className="ricerca-page__summary">
            {loading
              ? 'Ricerca in corso…'
              : `${data?.total ?? 0} risultat${(data?.total ?? 0) === 1 ? 'o' : 'i'}${data?.strutture ? ` in ${data.strutture} struttur${data.strutture === 1 ? 'a' : 'e'}` : ''}. Prezzi finali, slot aggiornati.`}
          </p>

          {error && <p className="ricerca-page__error" role="alert">{error}</p>}

          {!loading && results.length === 0 && !error && (
            <div className="ricerca-page__empty">
              <p>
                Nessun orario per «{queryKey.prestazione || 'la tua ricerca'}».
                Prova in video, allarga l&apos;area o cambia disciplina.
              </p>
              <div className="ricerca-page__empty-actions">
                <button type="button" className="vc-btn vc-btn-primary" onClick={() => setChip('modalita', 'video')}>
                  Solo in video
                </button>
                <Link to={publicPath('/medici')} className="vc-btn vc-btn-outline">Elenco medici</Link>
                <Link to={publicPath('/discipline')} className="vc-btn vc-btn-outline">Discipline</Link>
              </div>
            </div>
          )}

          {results.map((hit) => {
            const slug = hit.slug || getMedicoSlug({ id: hit.idMedico, nome: hit.medicoNome, cognome: hit.medicoCognome });
            const profilePath = publicPath(`/medici/${slug}`);
            const displayName = `${hit.medicoTitolo || 'Dott.'} ${hit.medicoNome || ''} ${hit.medicoCognome || ''}`.trim();
            return (
              <article key={`m-${hit.idMedico}-${hit.idTariffa}`} className="ricerca-card">
                <div className="ricerca-card__avatar" aria-hidden="true">
                  {hit.urlImg
                    ? <img src={hit.urlImg} alt="" width={72} height={72} loading="lazy" />
                    : initials(hit.medicoNome, hit.medicoCognome)}
                </div>
                <div className="ricerca-card__body">
                  <div className="ricerca-card__top">
                    <div>
                      <h2>
                        <Link to={profilePath}>{displayName}</Link>
                      </h2>
                      <p className="ricerca-card__spec">
                        {[hit.specializzazione, hit.nomePrestazione].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="ricerca-card__price">
                      <div>{formatPrice(hit.prezzo)}</div>
                      <div className="ricerca-card__price-sub">
                        {hit.isVideo ? 'videovisita' : (hit.nomePrestazione || 'visita')}
                      </div>
                    </div>
                  </div>

                  <div className="ricerca-card__meta">
                    {(hit.sedeNome || hit.strutturaNome) && (
                      <span>
                        {[hit.strutturaNome || hit.sedeNome, hit.sedeCitta].filter(Boolean).join(', ')}
                        {hit.distanzaKm != null ? `, ${hit.distanzaKm} km` : ''}
                      </span>
                    )}
                    <ModeTag mode={hit.isVideo ? 'video' : 'studio'} />
                  </div>

                  <div className="ricerca-card__tags">
                    <PayerTag
                      payer={hit.pagatore || 'PRIVATO'}
                      label={hit.pagatore === 'FONDO' && hit.fondoNome
                        ? `Fondo ${hit.fondoNome}`
                        : undefined}
                    />
                  </div>

                  <div className="ricerca-card__slots">
                    <span className="ricerca-card__slots-label">Prossimi orari</span>
                    <div className="ricerca-card__slot-row">
                      {(hit.prossimiSlot || []).map((slot) => (
                        <SlotButton
                          key={`${slot.data}-${slot.oraInizio}`}
                          time={slotChipLabel(slot)}
                          onClick={() => bookSlot(hit, slot)}
                        />
                      ))}
                      <Link to={profilePath} className="vc-slot-btn vc-slot-btn--ghost">Tutti gli orari</Link>
                    </div>
                    {(!hit.prossimiSlot || hit.prossimiSlot.length === 0) && (
                      <p className="ricerca-card__no-slot">Nessuno slot libero nei prossimi giorni.</p>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <aside className="ricerca-page__aside" aria-label="Aiuto alla lettura">
          <section className="ricerca-aside-card">
            <h2>Quanto paghi davvero</h2>
            <p>
              I prezzi mostrati sono quelli privati. Fondi e assicurazioni torneranno in ricerca
              quando i tariffari per compagnia saranno collegati.
            </p>
          </section>
          <section className="ricerca-aside-card">
            <h2>Come leggere i risultati</h2>
            <p><strong className="ricerca-aside-card__green">Verde</strong> sono orari liberi adesso: un tocco e prenoti.</p>
            <p><strong>Privato</strong> e <strong className="ricerca-aside-card__ssn">SSN</strong> (informativo) dicono il regime di pagamento, sempre prima del prezzo.</p>
          </section>
        </aside>
      </main>
    </div>
  );
}
